import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { Checkbox, Form } from 'semantic-ui-react';
import Link from '~/components/Link';
import Button from '~/components/Button';
import OwnershipDropdown from '~/components/OwnershipPicker/OwnershipDropdown';
import AdminNotesModal from '~/features/admin/companies/AdminNotesModal';
import { CompanyOwnershipDisplay, CompanyOwnershipDropdownOptions } from '~/constants';
import { getCompanyOwnership } from '~/api/admin';
import { getCompany, patchCompany } from '~/api/company';
import { useUpdateCompanyOwnership } from '~/mutations/admin/useUpdateCompanyOwnership';
import { usePermissions } from '~/requests/permissions/usePermissions';
import toast from 'react-hot-toast';
import type { Company } from '~/types/company';

import '~/components/OwnershipPicker/styles.scss';

interface CompanyProfileAdminTabProps {
  company: Company;
  onCompanyUpdated: (company: Company) => void;
}

export default function CompanyProfileAdminTab({ company, onCompanyUpdated }: CompanyProfileAdminTabProps) {
  const { id: companyId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { permissions } = usePermissions();
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [savingActive, setSavingActive] = useState(false);

  const { data: ownership, status } = useQuery({
    queryKey: ['companyOwnership', companyId],
    queryFn: () => getCompanyOwnership(companyId!),
    enabled: Boolean(companyId && permissions?.isAdmin),
    refetchOnWindowFocus: false,
  });

  const { mutateAsync: patchCompanyOwnership } = useUpdateCompanyOwnership({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyOwnership', companyId] });
      queryClient.invalidateQueries({ queryKey: ['ownership'] });
    },
  });

  const handleUpdate = async (key: string, value: string) => {
    if (!ownership?.companyId) {
      return;
    }
    await patchCompanyOwnership({ id: ownership.companyId, key, value });
    setOpenNotesModal(false);
  };

  const handleActiveToggle = async (_event: unknown, data: { checked?: boolean }) => {
    if (!companyId || typeof data.checked !== 'boolean') {
      return;
    }
    setSavingActive(true);
    try {
      await patchCompany(companyId, { active: data.checked });
      const fresh = await getCompany(companyId);
      onCompanyUpdated(fresh);
      toast.success(`Company ${data.checked ? 'activated' : 'deactivated'}.`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to update company status.');
    } finally {
      setSavingActive(false);
    }
  };

  const solarExperience = ownership?.solarExperience ?? '—';
  const thirdPartyExperience = ownership?.thirdPartyExperience ?? '—';

  if (!permissions.isAdmin) {
    return null;
  }

  if (status === 'loading') {
    return <p>Loading…</p>;
  }

  if (status === 'error' || !ownership) {
    return <p>Could not load admin fields for this company.</p>;
  }

  return (
    <div className='company-profile-admin-tab'>
      <div className='company-profile-admin-tab-heading'>Admin</div>
      <div className='company-profile-admin-tab-fields'>
        <Form.Field>
          <label style={{ marginRight: '8px' }}>Company status</label>
          <Checkbox
            toggle
            label={company.active === false ? 'Deactivated' : 'Active'}
            checked={company.active !== false}
            disabled={savingActive}
            onChange={handleActiveToggle}
          />
        </Form.Field>
        <OwnershipDropdown
          ownershipText={CompanyOwnershipDisplay.SOLAR_EXPERIENCE}
          defaultValue={solarExperience}
          buttonText={solarExperience}
          options={CompanyOwnershipDropdownOptions}
          onChange={(e, value) => handleUpdate('solarExperience', value)}
        />
        <OwnershipDropdown
          ownershipText={CompanyOwnershipDisplay.THIRD_PARTY_EXPERIENCE}
          defaultValue={thirdPartyExperience}
          buttonText={thirdPartyExperience}
          options={CompanyOwnershipDropdownOptions}
          onChange={(e, value) => handleUpdate('thirdPartyExperience', value)}
        />

        <div>
          <Link
            as={Button}
            underline
            bold
            style={{ fontSize: '0.875rem', fontStyle: 'italic' }}
            onClick={() => setOpenNotesModal(true)}
          >
            Admin notes
          </Link>
        </div>
      </div>
      {openNotesModal && (
        <AdminNotesModal
          company={ownership}
          closeModal={() => setOpenNotesModal(false)}
          onSave={notes => handleUpdate('notes', notes)}
        />
      )}
    </div>
  );
}
